import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Download, Palette, Image, Building2, 
  ChevronRight, Eye, History, Plus, Settings, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ReportsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  const [reportType, setReportType] = useState('seo_audit');
  const [reportTitle, setReportTitle] = useState('');
  const [branding, setBranding] = useState({
    company_name: '',
    logo_base64: null,
    primary_color: '#6366f1',
    secondary_color: '#8b5cf6',
    footer_text: ''
  });
  const [reportHistory, setReportHistory] = useState([]);
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadBranding();
    loadReportHistory();
  }, []);

  const loadBranding = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/branding`, { withCredentials: true });
      if (response.data.branding) {
        setBranding(prev => ({ ...prev, ...response.data.branding }));
      }
    } catch (error) {
      console.error('Failed to load branding:', error);
    }
  };

  const loadReportHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/history`, { withCredentials: true });
      setReportHistory(response.data.reports);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const saveBranding = async () => {
    try {
      await axios.post(`${API_URL}/api/reports/branding/save`, { branding }, { withCredentials: true });
      alert('Branding settings saved!');
    } catch (error) {
      alert('Failed to save branding');
    }
  };

  const generateReport = async () => {
    if (!reportTitle.trim()) {
      alert('Please enter a report title');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/reports/generate`, {
        report_type: reportType,
        title: reportTitle,
        branding: branding.company_name ? branding : null
      }, { withCredentials: true });
      
      setGeneratedHtml(response.data.html_content);
      setShowPreview(true);
      loadReportHistory();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    const blob = new Blob([generatedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportTitle.replace(/\s+/g, '_')}_report.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setBranding(prev => ({ ...prev, logo_base64: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const reportTypes = [
    { id: 'seo_audit', name: 'SEO Audit Report', icon: '🔍', description: 'Comprehensive SEO analysis' },
    { id: 'analytics', name: 'Analytics Report', icon: '📊', description: 'Traffic and user behavior' },
    { id: 'campaign_performance', name: 'Campaign Performance', icon: '📈', description: 'Ad campaign metrics' },
    { id: 'social_media', name: 'Social Media Report', icon: '📱', description: 'Social presence analysis' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700">
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
              <FileText className="w-8 h-8 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">White-label Reports</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'generate', label: 'Generate Report', icon: Plus },
            { id: 'branding', label: 'Branding Settings', icon: Palette },
            { id: 'history', label: 'Report History', icon: History }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Generate Report Tab */}
        {activeTab === 'generate' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Report</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Report Title</label>
                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Monthly SEO Report - Client Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Report Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {reportTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setReportType(type.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          reportType === type.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-2xl mb-2 block">{type.icon}</span>
                        <p className="font-medium text-gray-900">{type.name}</p>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={generateReport}
                  disabled={loading}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5" />
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Preview Section */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
                {generatedHtml && (
                  <button
                    onClick={downloadReport}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Download className="w-4 h-4" />
                    Download HTML
                  </button>
                )}
              </div>
              
              {generatedHtml ? (
                <div className="border rounded-lg overflow-hidden h-[500px]">
                  <iframe
                    srcDoc={generatedHtml}
                    className="w-full h-full"
                    title="Report Preview"
                  />
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg h-[500px] flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Eye className="w-12 h-12 mx-auto mb-2" />
                    <p>Report preview will appear here</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Branding Settings Tab */}
        {activeTab === 'branding' && (
          <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Branding Settings</h2>
            <p className="text-gray-500 mb-6">Customize your reports with your company branding</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  value={branding.company_name}
                  onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Your Company Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                <div className="flex items-center gap-4">
                  {branding.logo_base64 ? (
                    <img
                      src={`data:image/png;base64,${branding.logo_base64}`}
                      alt="Logo"
                      className="h-16 object-contain border rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center">
                      <Image className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="text-sm text-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                <input
                  type="text"
                  value={branding.footer_text}
                  onChange={(e) => setBranding(prev => ({ ...prev, footer_text: e.target.value }))}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  placeholder="© 2025 Your Company. All rights reserved."
                />
              </div>

              <button
                onClick={saveBranding}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Check className="w-5 h-5" />
                Save Branding Settings
              </button>
            </div>
          </div>
        )}

        {/* Report History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Report History</h2>
            </div>
            {reportHistory.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No reports generated yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {reportHistory.map(report => (
                  <div key={report.report_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{report.title}</p>
                        <p className="text-sm text-gray-500">
                          {report.report_type.replace('_', ' ')} • {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                      View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
